/**
 * The credit endpoints — slice 6's client surface.
 *
 *   fetchCreditBalances   GET   /credits/balances   this tenant's balance and reserved, every channel
 *   listCreditLedger      GET   /credits/ledger     the credit history screen, paginated and filterable
 *
 * **There is no write route here, and none belongs in this file.** Nothing in
 * this app reserves, consumes or releases a credit from an HTTP request: a
 * reservation is written by the send path in the same transaction as the
 * `messages` row, and a consume or release is written by whatever ingests a
 * provider's delivery webhook. Both are internal callers of the API's own
 * `CreditsService`, reached by dependency injection rather than a bearer
 * token — its controller documents the same absence, for the same reason.
 *
 * Every response is parsed against the shared read schema, exactly as
 * `campaigns.ts` does, so a contract drift shows up here rather than as
 * `undefined` in a component.
 */
import {
  creditBalancesResponseSchema,
  creditLedgerListResponseSchema,
  type CreditBalancesResponse,
  type CreditLedgerListQuery,
  type CreditLedgerListResponse,
} from '@wend/shared';

import { apiRequest } from './client';

/**
 * Every channel, always present, zeros for a workspace that has never had a
 * credit. `available` is not on the wire — it is `balance − reserved`, derived
 * with `availableCredits` from `@wend/shared` rather than trusted as a fourth
 * field this response could disagree with.
 */
export async function fetchCreditBalances(signal?: AbortSignal): Promise<CreditBalancesResponse> {
  return apiRequest({
    path: '/credits/balances',
    schema: creditBalancesResponseSchema,
    signal,
  });
}

/**
 * The credit history — paginated, and filterable by channel, entry type and
 * campaign.
 *
 * There is deliberately no client-side sum of these rows: the balance panel
 * reads `credit_balances` directly, and a screen that also added up a page of
 * the ledger to check it would be inventing a second opinion about a number the
 * background reconciliation already exists to verify — over the whole ledger,
 * which one page of it is not.
 *
 * That reconciliation runs **hourly**, and this comment promised it once a night
 * until slice 17: `RECONCILE_INTERVAL_MS` is `60 * 60_000` in
 * `credit-reconciliation.scheduler.ts`, and that file argues for the hour on the
 * grounds that a wrong balance is money being wrong. Nothing in this file
 * depends on the interval; what depends on it is the temptation to add the
 * client-side sum, and a whole night is twelve times more tempting than the
 * truth. The wrong word is not written out here, so the ban in
 * `verify-page-hardening.tsx` can be flat.
 */
export async function listCreditLedger(
  query: CreditLedgerListQuery,
  signal?: AbortSignal,
): Promise<CreditLedgerListResponse> {
  return apiRequest({
    path: '/credits/ledger',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      ...(query.channel === undefined ? {} : { channel: query.channel }),
      ...(query.type === undefined ? {} : { type: query.type }),
      ...(query.campaignId === undefined ? {} : { campaignId: query.campaignId }),
    },
    schema: creditLedgerListResponseSchema,
    signal,
  });
}
